import { useEffect, useMemo } from 'react';
import type L from 'leaflet';
import { latLngBounds } from 'leaflet';
import {
  GeoJSON,
  MapContainer,
  Marker,
  TileLayer,
  Tooltip,
  useMap,
} from 'react-leaflet';
import type { Point } from '@/lib/api.types';
import {
  FIT_MIN_ZOOM,
  FIT_PADDING_BOTTOM_RIGHT,
  FIT_PADDING_TOP_LEFT,
  type MapSpec,
  type MarkerSpec,
} from '@/lib/map-spec';
import { amenityIconMarker, dotMarkerIcon, ienoneMarkerIcon, priceBalloonIcon } from '@/lib/leaflet-setup';
import { GSI_ATTRIBUTION_HTML, tileMaxNativeZoom } from '@/lib/tiles';
import styles from './OverviewMiniMap.module.css';

export type { GeometryLayer, MarkerSpec } from '@/lib/map-spec';

// MarkerSpec → L.DivIcon。同じ (icon|label, side, color) は 1 つの DivIcon を共有する。毎レンダーで新しい
// DivIcon を作ると react-leaflet が setIcon し直し、ViewUpdater の fitBounds も走り直すため。
// label は点ごとに違う (価格) ので、地点を変えて調べ続けても際限なく増えないよう上限で空にする。
const iconCache = new Map<string, L.DivIcon>();
const ICON_CACHE_MAX = 1000;

function divIconFor(m: MarkerSpec): L.DivIcon {
  const key = `${m.icon ?? ''}|${m.label ?? ''}|${m.labelSide ?? ''}|${m.color}`;
  let icon = iconCache.get(key);
  if (!icon) {
    if (iconCache.size >= ICON_CACHE_MAX) iconCache.clear();
    icon = m.icon
      ? amenityIconMarker(m.icon, m.color)
      : m.label
        ? priceBalloonIcon(m.label, m.color, m.labelSide)
        : dotMarkerIcon(m.color);
    iconCache.set(key, icon);
  }
  return icon;
}

type Props = {
  spec: MapSpec;
  tileAttribution?: string;
};

// fitToMarkers の規則 (FIT_*) は lib/map-spec.ts。収まらない遠方ピンはクリップを容認する(距離は凡例が伝える)。
// PDF のラスタライザ (pdf/render-map.ts) も同じ規則で表示範囲を決める。

function ViewUpdater({
  point,
  zoom,
  markers,
  fitToMarkers,
}: {
  point: Point;
  zoom: number;
  markers: MarkerSpec[];
  fitToMarkers?: boolean;
}) {
  const map = useMap();
  useEffect(() => {
    if (fitToMarkers && markers.length > 0) {
      const bounds = latLngBounds([[point.lat, point.lon]]);
      for (const m of markers) bounds.extend([m.point.lat, m.point.lon]);
      // 余白はオーバーレイ(左上キャプション・右下凡例)とピンの重なりを避ける分を取る。
      map.fitBounds(bounds, {
        paddingTopLeft: FIT_PADDING_TOP_LEFT,
        paddingBottomRight: FIT_PADDING_BOTTOM_RIGHT,
        maxZoom: zoom,
        animate: false,
      });
      if (map.getZoom() < FIT_MIN_ZOOM) {
        map.setView([point.lat, point.lon], FIT_MIN_ZOOM, { animate: false });
      }
      return;
    }
    map.setView([point.lat, point.lon], zoom, { animate: false });
  }, [map, point.lat, point.lon, zoom, markers, fitToMarkers]);
  return null;
}

// 章の非対話ミニマップ。MapSpec (lib/map-spec.ts) を Leaflet で描く。同じ MapSpec を PDF 冊子の
// ラスタライザも描くので、見た目の規則 (マーカー・中心ピン・fit) はここと pdf/render-map.ts で揃える。
export default function OverviewMiniMap({ spec, tileAttribution = GSI_ATTRIBUTION_HTML }: Props) {
  const {
    point,
    tileUrl,
    overlayTileUrl,
    overlayOpacity = 0.65,
    zoom,
    height,
    markers,
    geometries,
    showCenterMarker = true,
    fitToMarkers = false,
  } = spec;
  const leafletMarkers = useMemo(
    () => markers.map((m) => ({ ...m, divIcon: divIconFor(m) })),
    [markers],
  );
  return (
    <div className={styles.wrap} style={{ height }}>
      <MapContainer
        center={[point.lat, point.lon]}
        zoom={zoom}
        scrollWheelZoom={false}
        dragging={false}
        doubleClickZoom={false}
        touchZoom={false}
        zoomControl={false}
        keyboard={false}
        boxZoom={false}
        attributionControl
        className={styles.map}
      >
        {/* crossOrigin: PDF 用にタイルを canvas へ描くため、アプリ内の全タイル要求を CORS モードに統一する
            (同じ URL を non-CORS で先に読むと Safari のキャッシュが流用され canvas が汚染される)。 */}
        <TileLayer
          url={tileUrl}
          attribution={tileAttribution}
          maxNativeZoom={tileMaxNativeZoom(tileUrl)}
          maxZoom={18}
          crossOrigin="anonymous"
        />
        {overlayTileUrl && (
          // Official GSI hazard rasters cover z2-17; upscale beyond their native max so the
          // overlay stays aligned with the base when the map is displayed at z>17.
          <TileLayer
            url={overlayTileUrl}
            maxNativeZoom={tileMaxNativeZoom(overlayTileUrl)}
            maxZoom={18}
            opacity={overlayOpacity}
            crossOrigin="anonymous"
          />
        )}
        <ViewUpdater
          point={point}
          zoom={zoom}
          markers={markers}
          fitToMarkers={fitToMarkers}
        />

        {geometries.map((g, i) => (
          <GeoJSON
            key={`g-${i}-${JSON.stringify(g.data).slice(0, 64)}`}
            data={{ type: 'Feature', properties: {}, geometry: g.data } as GeoJSON.Feature}
            style={g.style}
          >
            {g.label && (
              <Tooltip sticky direction="top" opacity={0.95}>
                {g.label}
              </Tooltip>
            )}
          </GeoJSON>
        ))}

        {leafletMarkers.map((m, i) => (
          <Marker key={i} position={[m.point.lat, m.point.lon]} icon={m.divIcon}>
            {m.title && (
              <Tooltip direction="top" offset={[0, -10]} opacity={0.95}>
                {m.title}
              </Tooltip>
            )}
          </Marker>
        ))}

        {showCenterMarker && (
          // 調査地点は常に最前面 (価格の丸が南側にあっても隠されない)。PDF も最後に描く。
          <Marker position={[point.lat, point.lon]} icon={ienoneMarkerIcon} zIndexOffset={1000} />
        )}
      </MapContainer>
    </div>
  );
}
